// src/frontend/js/modern/core/storage/resilient-storage.js
//
// Resilient storage layer.
//
// Primary memory : IndexedDB (via the idb-like driver).
// Fallback       : in-RAM volatile queue (private browsing, corrupted/evicted
//                  IDB, unsupported browsers). Switching is automatic and
//                  silent — no exception ever escapes this layer.
// Health monitor : periodic sentinel write/read plus a "previous sentinel"
//                  disappearance check that detects sudden browser-side wipes
//                  (e.g. Safari ITP / private-mode eviction). Recovery probes
//                  can promote the layer back to IDB and flush the journal.
//
// State broadcasting: every mode change dispatches a custom DOM event
//   `wlfmc:storage-state` (detail: {mode, volatile, reason})
// and an eventBus `storage:state` so the UI can show an appropriate warning.

import { eventBus } from '../event-bus.js';
import { openDB, isIndexedDBSupported, KV_STORE, deleteDB } from './idb-driver.js';

const HEALTH_KEY = '_wlfmc_health';
const PREV_HEALTH_KEY = '_wlfmc_health_prev';
const HEALTH_INTERVAL_MS = 45000;
const RECOVERY_EVERY = 5; // every Nth tick while degraded

class ResilientStorage {
	constructor() {
		this._db = null;
		this._mode = 'idle';            // 'idle' | 'idb' | 'memory'
		this._reason = '';
		this._memory = new Map();       // volatile backing store
		this._journal = [];             // queued writes while degraded
		this._timer = null;
		this._tick = 0;
		this._initialized = false;
	}

	/* ── Lifecycle ─────────────────────────────────────────────────────── */

	/**
	 * Open the durable backend or silently fall back to RAM.
	 * Never throws.
	 */
	/**
	 * Open the durable backend or silently fall back to RAM.
	 * If the database is corrupted (e.g., missing object stores after
	 * manual deletion or partial upgrade), it self-heals by deleting
	 * and recreating the database.
	 * Never throws.
	 */
	async init() {
		if (this._initialized) return;
		this._initialized = true;
		try {
			if (!isIndexedDBSupported()) throw new Error('unsupported');
			this._db = await openDB();
			// Initial probe: write + read back the sentinel.
			await this._writeSentinel(HEALTH_KEY);
			const readBack = await this._db.get(KV_STORE, HEALTH_KEY);
			if (!readBack || !readBack.t) throw new Error('probe-failed');
			this._setMode('idb', '');
			this._startHealthLoop();
		} catch (error) {
			// Self-heal: if the DB exists but is broken (missing stores,
			// partial upgrade), delete it and try once more.
			const isCorrupt = error?.name === 'NotFoundError' ||
				(error?.message || '').includes('not a known object store');
			if (isCorrupt) {
				try {
					if (this._db) { this._db.close(); this._db = null; }
					await deleteDB();
					this._db = await openDB();
					await this._writeSentinel(HEALTH_KEY);
					const check = await this._db.get(KV_STORE, HEALTH_KEY);
					if (check && check.t) {
						this._setMode('idb', 'self-healed');
						this._startHealthLoop();
						return;
					}
				} catch (retryError) {
					console.error('[ResilientStorage] Self-heal failed:', retryError);
				}
			}
			console.error('[ResilientStorage] IDB initialization failed:', error);
			this._degrade(this._reasonFor(error), true);
			this._startHealthLoop();
		}
	}

	/* ── Public API (always resolves; never throws) ─────────────────────── */

	getMode() {
		return this._mode;
	}

	isVolatile() {
		return this._mode !== 'idb';
	}

	async get(key) {
		if (this._mode === 'idb') {
			try {
				return await this._db.get(KV_STORE, key);
			} catch (e) {
				this._degrade('runtime-error');
				// fall through to memory (may hold journaled newer value)
			}
		}
		return this._memory.has(key) ? this._memory.get(key) : undefined;
	}

	async set(key, value) {
		if (this._mode === 'idb') {
			try {
				await this._db.put(KV_STORE, value, key);
				return true;
			} catch (e) {
				this._degrade('runtime-error');
			}
		}
		// Volatile path: keep value AND journal it for potential recovery.
		this._memory.set(key, value);
		this._journal.push({ key, value, t: Date.now() });
		if (this._journal.length > 500) this._journal.splice(0, this._journal.length - 500);
		return false;
	}

	async remove(key) {
		if (this._mode === 'idb') {
			try {
				await this._db.delete(KV_STORE, key);
				return;
			} catch (e) {
				this._degrade('runtime-error');
			}
		}
		this._memory.delete(key);
		this._journal.push({ key, value: undefined, t: Date.now(), deleted: true });
	}

	async keys() {
		if (this._mode === 'idb') {
			try {
				return (await this._db.getAllKeys(KV_STORE)) || [];
			} catch (e) {
				this._degrade('runtime-error');
			}
		}
		return Array.from(this._memory.keys());
	}

	/**
	 * Read every non-internal key (used for boot recovery).
	 */
	async exportAll() {
		const keys = await this.keys();
		const out = new Map();
		for (const key of keys) {
			if (String(key).startsWith('_')) continue;
			out.set(key, await this.get(key));
		}
		return out;
	}

	/* ── Health monitoring ─────────────────────────────────────────────── */

	_startHealthLoop() {
		if (this._timer) clearInterval(this._timer);
		this._tick = 0;
		this._timer = setInterval(() => this._healthTick(), HEALTH_INTERVAL_MS);
	}

	async _healthTick() {
		this._tick++;

		if (this._mode === 'idb') {
			try {
				// 1) Detect sudden eviction: previous sentinel must still exist.
				const prev = await this._db.get(KV_STORE, PREV_HEALTH_KEY);
				if (prev && prev.t && !(await this._db.get(KV_STORE, HEALTH_KEY))) {
					throw new Error('evicted');
				}
				// 2) Roll sentinels & write a fresh one (also exercises writes).
				const current = await this._db.get(KV_STORE, HEALTH_KEY);
				await this._db.put(KV_STORE, current || { t: Date.now() }, PREV_HEALTH_KEY);
				await this._writeSentinel(HEALTH_KEY);
				return; // healthy
			} catch (error) {
				this._degrade('evicted');
			}
		}

		// Degraded: periodically retry promotion to IDB.
		if (this._mode === 'memory' && this._tick % RECOVERY_EVERY === 0) {
			await this._tryRecovery();
		}
	}

	async _tryRecovery() {
		try {
			this._db = await openDB();
			await this._writeSentinel(HEALTH_KEY);
			const readBack = await this._db.get(KV_STORE, HEALTH_KEY);
			if (!readBack || !readBack.t) throw new Error('probe-failed');

			// Flush the RAM journal into IDB (newest wins per key).
			for (let i = this._journal.length - 1; i >= 0; i--) {
				const entry = this._journal[i];
				if (entry.deleted) {
					await this._db.delete(KV_STORE, entry.key).catch(() => {});
				} else {
					await this._db.put(KV_STORE, entry.value, entry.key).catch(() => {});
				}
			}
			this._journal.length = 0;

			// Adopt anything already mirrored into RAM map as well.
			for (const [key, value] of this._memory.entries()) {
				await this._db.put(KV_STORE, value, key).catch(() => {});
			}

			this._setMode('idb', 'recovered');
		} catch (e) {
			// Stay degraded; try again on a future tick.
		}
	}

	_writeSentinel(key) {
		return this._db.put(KV_STORE, { t: Date.now() }, key);
	}

	/* ── Mode transitions & notifications ──────────────────────────────── */

	_reasonFor(error) {
		const msg = (error && error.message) || '';
		if ('unsupported' === msg) return 'unsupported';
		if (/blocked|version/i.test(msg)) return 'blocked';
		if (/quota/i.test(msg)) return 'quota';
		return 'open-failed';
	}

	_degrade(reason, initial = false) {
		this._mode = 'memory';
		this._reason = reason;
		this._notify(initial ? `initial (${reason})` : reason);
	}

	_setMode(mode, reason = '') {
		this._mode = mode;
		this._reason = reason;
		this._notify(reason);
	}

	_notify(reason) {
		const detail = {
			mode: this._mode,
			volatile: this.isVolatile(),
			reason,
		};
		window.wlfmcStorageMode = detail.mode;
		try {
			document.dispatchEvent(new CustomEvent('wlfmc:storage-state', { detail }));
		} catch (e) { /* noop */ }
		eventBus.emit('storage:state', detail);
		console.warn(`[ResilientStorage] mode=${detail.mode} (${reason})`);
	}
}

export const resilientStorage = new ResilientStorage();
export default resilientStorage;
